import { plainError, type PlainError } from "@/lib/plainError";

import type { CasePrepUscisFilledForm } from "./types";
import { countAnsweredFields } from "./utils";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "source 3 of 8 · 41 fields" while a fill runs; null otherwise. */
export function fillProgressDetail(
  form: CasePrepUscisFilledForm
): string | null {
  if (form.status !== "generating") return null;
  if (form.run_stalled) return "stopped responding";
  const progress = form.run_progress;
  if (!progress || progress.steps_total <= 0) return null;
  const current = Math.min(progress.steps_done + 1, progress.steps_total);
  return `source ${current} of ${progress.steps_total} · ${plural(
    progress.fields_filled,
    "field"
  )}`;
}

/** "Filling I-140 · source 3 of 8 · 41 fields". */
export function fillingLabel(form: CasePrepUscisFilledForm): string {
  const detail = fillProgressDetail(form);
  const head = `Filling ${form.form_number}`;
  return detail ? `${head} · ${detail}` : head;
}

/** Never filled: no answers and no run behind it, whatever its PDF pointer says. */
export function isNeverFilled(form: CasePrepUscisFilledForm): boolean {
  if (form.status === "not_started") return true;
  return (
    form.status !== "generating" &&
    countAnsweredFields(form) === 0 &&
    (form.run_steps ?? []).length === 0
  );
}

/**
 * Where a form's fill stands, as the form screen shows it: `filling` in the
 * background, `failed` (or stopped responding) with nothing in it, `unfilled`
 * with no answer yet, or `filled` with at least one.
 */
export type FormFillState = "filling" | "failed" | "unfilled" | "filled";

export type FormFill = {
  formNumber: string;
  state: FormFillState;
  answered: number;
  /** The form's fields; null until its first fill or blank copy. */
  total: number | null;
  /** Never filled nor run: the form the case was created with. */
  notStarted: boolean;
  /** "Filling I-140 · source 3 of 8 · 41 fields" while filling. */
  progress: string | null;
  /** Why the last fill failed, in plain words and never cut. */
  error: string | null;
  /** The server's own wording, when `error` had to say it plainly. */
  errorDetail: string | null;
};

/** Why the form's last fill failed: its error, a failed source's, or Extend's. */
const failureOf = (form: CasePrepUscisFilledForm): PlainError | null => {
  const step = (form.run_steps ?? []).find((s) => s.status === "failed");
  const raw =
    form.last_run_error ||
    step?.failure_message ||
    step?.failure_reason ||
    form.extend?.failure_message ||
    form.extend?.failure_reason;
  return plainError(raw);
};

/** The form's fill, read the way the form screen reads it. */
export function formFill(form: CasePrepUscisFilledForm): FormFill {
  const answered = countAnsweredFields(form);
  const fields = form.overlay_fields?.length ?? 0;
  const base = {
    formNumber: form.form_number,
    answered: fields ? Math.min(answered, fields) : answered,
    total: fields || null,
    notStarted: false,
    progress: null,
    error: null,
    errorDetail: null
  };
  if (form.status === "generating")
    return form.run_stalled
      ? { ...base, state: "failed", error: "the fill stopped responding" }
      : { ...base, state: "filling", progress: fillingLabel(form) };
  const failure = failureOf(form);
  const why = {
    error: failure?.text ?? null,
    errorDetail: failure?.detail ?? null
  };
  if (form.status === "failed" || (failure && !answered))
    return {
      ...base,
      ...why,
      state: "failed",
      error: why.error ?? "the fill failed"
    };
  if (answered)
    return {
      ...base,
      state: "filled",
      ...(form.last_run_error ? why : {})
    };
  return { ...base, state: "unfilled", notStarted: isNeverFilled(form) };
}

export type FillOutcome = {
  tone: "success" | "warning";
  title: string;
  description?: string;
};

/** The toast for a fill that just ended (`before` was generating, `after` is not). */
export function fillOutcome(
  before: Pick<CasePrepUscisFilledForm, "status"> | undefined,
  after: CasePrepUscisFilledForm
): FillOutcome | null {
  if (before?.status !== "generating" || after.status === "generating") {
    return null;
  }
  if (after.last_run_error || after.status === "failed") {
    return {
      tone: "warning",
      title: `${after.form_number} wasn't filled`,
      description:
        after.last_run_error ??
        "No source produced a filled PDF. The form is as it was before."
    };
  }
  const review = (after.needs_input ?? []).length;
  return {
    tone: "success",
    title: `${after.form_number} filled`,
    description: review
      ? `${plural(review, "field")} need${review === 1 ? "s" : ""} review.`
      : `${plural(countAnsweredFields(after), "field")} answered.`
  };
}
